import {
  NavContext,
  useIonRouter,
  type UseIonRouterResult,
} from '@ionic/react';
import { useContext, useMemo } from 'react';

export type IonBackRouter = Pick<
  UseIonRouterResult,
  'canGoBack' | 'push' | 'routeInfo'
> & {
  /**
   * Ionic NavContext back. Pops the stack; when Ionic finds no entry to pop
   * to, it opens `defaultHref` instead (`useIonRouter().goBack()` passes
   * none and silently does nothing in that case).
   */
  goBack: (defaultHref?: string) => void;
};

/**
 * Where to go when the stack can't be popped: a path (opened with the back
 * animation), or a custom navigation plus the path Ionic should use if its
 * own back finds no previous entry.
 */
export type IonBackFallback = string | { href: string; navigate: () => void };

/** `useIonRouter()` with a back that accepts Ionic's `defaultHref`. */
export function useIonBackRouter(): IonBackRouter {
  const ionRouter = useIonRouter();
  const nav = useContext(NavContext);
  return useMemo(
    () => ({
      canGoBack: ionRouter.canGoBack,
      push: ionRouter.push,
      routeInfo: ionRouter.routeInfo,
      goBack: (defaultHref?: string) => nav.goBack(defaultHref),
    }),
    [ionRouter.canGoBack, ionRouter.push, ionRouter.routeInfo, nav],
  );
}

/**
 * True when Ionic `goBack()` has a route to pop to.
 *
 * `canGoBack()` only checks `locationHistory.length > 1`. Tab switches and
 * replace navigations keep entries without a `pushedByRoute`, and
 * @ionic/react-router `handleNavigateBack` silently does nothing for those
 * when no default href is given (DEV-514).
 */
export function canPopIonHistory(router: IonBackRouter): boolean {
  const pushedByRoute = router.routeInfo?.pushedByRoute;
  return (
    router.canGoBack() &&
    Boolean(pushedByRoute) &&
    pushedByRoute !== router.routeInfo?.pathname
  );
}

/**
 * Pop the Ionic stack when this page was pushed; otherwise go to `fallback`.
 * The fallback path is also handed to Ionic's back as `defaultHref`, for when
 * the pushing route is no longer in Ionic's history (e.g. after a tab reset).
 */
export function goBackOrFallback(
  router: IonBackRouter,
  fallback: IonBackFallback,
): void {
  const href = typeof fallback === 'string' ? fallback : fallback.href;
  if (canPopIonHistory(router)) {
    router.goBack(href);
    return;
  }
  if (typeof fallback !== 'string') {
    fallback.navigate();
    return;
  }
  router.push(fallback, 'back', 'pop');
}
